/** Matrix-style live activity strip — falling cyan digits showing real-time
 *  listener-count snapshots for every tracked station. Drop on the
 *  station-dashboard Overview tab as a hero strip. */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

const C = T.dark;
const COLS = 60;

export function MatrixActivity({ height = 160 }) {
  const canvasRef = useRef(null);
  const [stations, setStations] = useState([]);
  const [tickerEvents, setTickerEvents] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get("/realtime/pulse");
        setStations(r.data.top_stations || []);
      } catch {}
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  // Render Matrix rain
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || stations.length === 0) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);

    const colWidth = canvas.offsetWidth / COLS;
    const drops = Array.from({ length: COLS }, () => Math.random() * canvas.offsetHeight);
    let raf;

    const draw = () => {
      ctx.fillStyle = "rgba(8,13,26,0.18)";
      ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      ctx.font = "11px 'DM Mono', Menlo, monospace";

      for (let i = 0; i < COLS; i++) {
        const station = stations[i % stations.length];
        if (!station) continue;
        const s = String(station.estimated_total_people || 0);
        const ch = s[Math.floor(Math.random() * s.length)] ||
                   String.fromCharCode(0x30A0 + Math.random() * 96);
        // Head bright, tail faded
        ctx.fillStyle = i % 7 === 0 ? "#00D4FF" : (i % 3 === 0 ? "#00C896" : "#0EA5E9");
        ctx.shadowColor = "#00D4FF";
        ctx.shadowBlur = i % 7 === 0 ? 8 : 0;
        ctx.fillText(ch, i * colWidth, drops[i]);
        if (drops[i] > canvas.offsetHeight && Math.random() > 0.975) drops[i] = 0;
        drops[i] += 16;
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [stations]);

  return (
    <div data-testid="matrix-activity" style={{ position: "relative", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
      <canvas ref={canvasRef} style={{ width: "100%", height, display: "block" }} />
      <div style={{ position: "absolute", inset: 0, padding: "16px 22px",
        background: "linear-gradient(90deg,rgba(8,13,26,0.85) 0%,rgba(8,13,26,0.3) 60%,transparent 100%)",
        display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: C.cyan, fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", fontFamily: "'DM Mono',monospace" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981", animation: "blinkM 1.4s infinite" }} />
            REAL-TIME ACTIVITY MATRIX
          </div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginTop: 4 }}>
            {stations.length} stations publishing live counts · {(stations.reduce((a, s) => a + (s.raw_connections || 0), 0)).toLocaleString()} live connections
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, color: C.muted, fontSize: 10, fontFamily: "'DM Mono',monospace", flexWrap: "wrap" }}>
          <span>↳ stream telemetry · 60s cycle</span>
          <span style={{ color: C.cyan }}>↳ DIRECT</span>
          <span style={{ color: "#F59E0B" }}>↳ HYBRID</span>
          <span style={{ color: "#3B82F6" }}>↳ MODEL</span>
        </div>
      </div>
      <style>{`@keyframes blinkM { 0%,100% { opacity:1 } 50% { opacity:0.3 } }`}</style>
    </div>
  );
}
